import React, { memo, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Container,
  Typography,
  Grid,
  Stack,
  Link,
  IconButton
} from "@mui/material";
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { FaFacebook, FaInstagram } from 'react-icons/fa';
import { trackButtonClick } from '../../utils/gtm';

const Footer = () => {
  const navigate = useNavigate();

  const handleLinkClick = (href, label) => {
    const buttonName = `footer_${label.toLowerCase().replace(/\s+/g, '_')}`;
    
    if (href.startsWith('#')) {
      // Anchor link - scroll to section
      trackButtonClick(buttonName, 'footer_landing_page', {
        link_type: 'anchor',
        link_url: href
      });
      document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
    } else if (href.startsWith('http')) {
      // External link
      trackButtonClick(buttonName, 'footer_landing_page', {
        link_type: 'external',
        link_url: href
      });
    } else {
      // Internal route
      trackButtonClick(buttonName, 'footer_landing_page', {
        link_type: 'internal',
        link_url: href
      });
      navigate(href);
    }
  };

  const handleSocialClick = (href, platform) => {
    trackButtonClick(`social_${platform.toLowerCase()}`, 'footer_landing_page', {
      social_platform: platform,
      link_url: href
    });
  };

  const footerSections = useMemo(() => ([
    {
      title: "Product",
      links: [
        { label: "Features", href: "#features" },
        { label: "Pricing", href: "#pricing" },
        // { label: "Documentation", },
        // { label: "API", }
      ]
    },
    {
      title: "Company",
      links: [
        { label: "Blog", href: "/blog" },
        { label: "Contact", href: "/contact" },
        { label: "What We Do", href: "/what-we-do" },
      ]
    },
    {
      title: "Support",
      links: [
        { label: "Help Center", href: "/support" },
        // { label: "Community", },
        // { label: "Status", },
        // { label: "GitHub", }
      ]
    }
  ]), []);

  const socialLinks = useMemo(() => ([
    { icon: TwitterIcon, href: "https://x.com/Sniffr_ai", label: "Twitter", isMui: true },
    { icon: LinkedInIcon, href: "https://www.linkedin.com/company/sniffr-ai", label: "LinkedIn", isMui: true },
    { icon: FaFacebook, href: "https://www.facebook.com/sniffrai", label: "Facebook", isMui: false },
    { icon: FaInstagram, href: "https://www.instagram.com/sniffr.ai/", label: "Instagram", isMui: false },
    { icon: YouTubeIcon, href: "https://www.youtube.com/@Sniffrai", label: "YouTube", isMui: true },
  ]), []);

  return (
    <Box sx={{ bgcolor: "#0b0b0c", pt: 10, pb: 10}}>
      <Container
        maxWidth="xl"
        sx={{
          px: { xs: 3, sm: 4, md: 6, lg: 20, xl: 20 },
          maxWidth: { xs: "100%", sm: "100%", md: "100%", lg: "1440px", xl: "1600px" },
          mx: "auto",
          textAlign: "start",
          fontFamily: "MD IO, monospace"
        }}
      >
        <Grid container spacing={{ xs: 4, sm: 5, md: 6, lg: 8 }}>
          {/* Logo and Description */}
          <Grid item xs={12} md={4}>
            <Box sx={{ mb: { xs: 3, md: 4 } }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: { xs: 3, md: 4 } }}>
                <picture style={{ display: 'block', lineHeight: 0 }}>
                  <source srcSet="/assets/logo.avif" type="image/avif" />
                  <source srcSet="/assets/logo.webp" type="image/webp" />
                  <Box component="img" src="/assets/logo.png" alt="Sniffr.ai Logo"
                    sx={{ height: 30, width: 30, borderRadius: "50%", transition: 'transform 0.2s ease', '&:hover': { transform: 'scale(1.05)' } }}
                  />
                </picture>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    background: "linear-gradient(90deg, #FF8052 0%, #00BFFF 50%, #B366FF 100%)",
                    backgroundClip: "text",
                    color: "transparent",
                    fontSize: "1.5rem",
                    letterSpacing: "-0.025em",
                    fontFamily: "MD IO, monospace"
                  }}
                >
                  Sniffr.ai
                </Typography>
              </Box>

              <Typography
                variant="body2"
                sx={{
                  color: "#9ca3af",
                  lineHeight: 1.6,
                  mb: { xs: 4, md: 5 },
                  maxWidth: { xs: "280px", md: "320px" },
                  fontFamily: "Segoe UI, monospace"
                }}
              >
                AI-powered code review and requirement validation for modern development teams.
              </Typography>

              {/* Social Media Icons */}
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                {socialLinks.map((social, index) => {
                  const IconComponent = social.icon;
                  return (
                    <IconButton
                      key={index}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        e.preventDefault();
                        handleSocialClick(social.href, social.label);
                        window.open(social.href, '_blank', 'noopener,noreferrer');
                      }}
                      sx={{
                        color: "#d1d5db",
                        width: 40,
                        height: 40,
                        border: "1px solid rgba(255,255,255,0.12)",
                        borderRadius: 20,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          color: "#ffffff",
                          borderColor: "rgba(255,255,255,0.35)",
                          transform: "translateY(-1px)"
                        }
                      }}
                      aria-label={social.label}
                    >
                      {social.isMui
                        ? <IconComponent sx={{ fontSize: 18 }} />
                        : <IconComponent size={18} />
                      }
                    </IconButton>
                  );
                })}
              </Stack>
            </Box>
          </Grid>

          {/* Footer Sections */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={{ xs: 4, sm: 3, md: 4 }}>
              {footerSections.map((section, index) => (
                <Grid item xs={12} sm={4} key={index}>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "#ffffff",
                        fontSize: "1rem",
                        mb: { xs: 3, md: 4 },
                        letterSpacing: "-0.025em",
                        fontFamily: "Segoe UI, monospace"
                      }}
                    >
                      {section.title}
                    </Typography>
                    <Stack spacing={{ xs: 2.5, md: 3 }}>
                      {section.links.map((link, linkIndex) => (
                        <Link
                          key={linkIndex}
                          // href={link.href}
                          onClick={(e) => {
                            e.preventDefault();
                            handleLinkClick(link.href, link.label);
                          }}
                          sx={{
                            color: "#9ca3af",
                            textDecoration: "none",
                            fontSize: "0.875rem",
                            lineHeight: 1.5,
                            transition: "all 0.2s ease",
                            cursor: "pointer",
                            "&:hover": {
                              color: "#ffffff",
                              transform: "translateX(2px)"
                            }
                          }}
                        >
                          {link.label}
                        </Link>
                      ))}
                    </Stack>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>

        {/* Bottom Section */}
        <Box
          sx={{
            mt: 4,
            pt: 2,
            borderTop: "1px solid rgba(255,255,255,0.08)",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: { xs: 3, sm: 0 }
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#9ca3af",
              fontSize: "0.875rem",
              fontFamily: "Segoe UI, monospace"
            }}
          >
            © 2025 Sniffr.ai. All rights reserved.
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 2, sm: 4 }}
            alignItems={{ xs: "flex-start", sm: "center" }}
          >
            <Link
              href="/terms-of-use"
              onClick={(e) => {
                e.preventDefault();
                handleLinkClick('/terms-of-use', 'Terms of Service');
              }}
              sx={{
                color: "#9ca3af",
                textDecoration: "none",
                fontSize: "0.875rem",
                transition: "color 0.2s ease",
                cursor: "pointer",
                "&:hover": { color: "#ffffff" },
                fontFamily: "Segoe UI, monospace"
              }}
            >
              Terms of Service
            </Link>
            <Link
              href="/privacy-policy"
              onClick={(e) => {
                e.preventDefault();
                handleLinkClick('/privacy-policy', 'Privacy Policy');
              }}
              sx={{
                color: "#9ca3af",
                textDecoration: "none",
                fontSize: "0.875rem",
                transition: "color 0.2s ease",
                cursor: "pointer",
                "&:hover": { color: "#ffffff" },
                fontFamily: "Segoe UI, monospace"
              }}
            >
              Privacy Policy
            </Link>
            <Link
              href="/cookie-policy"
              onClick={(e) => {
                e.preventDefault();
                handleLinkClick('/cookie-policy', 'Cookie Policy');
              }}
              sx={{
                color: "#9ca3af",
                textDecoration: "none",
                fontSize: "0.875rem",
                transition: "color 0.2s ease",
                cursor: "pointer",
                "&:hover": { color: "#ffffff" },
                fontFamily: "Segoe UI, monospace"
              }}
            >
              Cookie Policy
            </Link>
            <Link
              href="/service-level-agreement"
              onClick={(e) => {
                e.preventDefault();
                handleLinkClick('/service-level-agreement', 'SLA');
              }}
              sx={{
                color: "#9ca3af",
                textDecoration: "none",
                fontSize: "0.875rem",
                transition: "color 0.2s ease",
                cursor: "pointer",
                "&:hover": { color: "#ffffff" },
                fontFamily: "Segoe UI, monospace"
              }}
            >
              SLA
            </Link>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default memo(Footer);